import React, { useState } from "react";
import { Modal, Button, Upload, message, Space, Image, Row, Col } from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import "./LogoUpload.scss";

const LogoUpload = ({ logoUrl, onUpload, uploading = false, style = {} }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const validateFileType = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic"
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  };

  const validateFileSize = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  const beforeUpload = (file) => {
    if (!validateFileType(file)) {
      message.error("Only JPG, JPEG, PNG, WebP, and HEIC image files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (!validateFileSize(file)) {
      message.error("File size must be less than 5MB");
      return Upload.LIST_IGNORE;
    }

    setSelectedFile(file);
    setShowUploadModal(true);
    return false; // Prevent automatic upload
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      message.error("Please select a logo file");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("logo", selectedFile);

      await onUpload(formData);
      message.success("Logo uploaded successfully!");

      // Reset modal state
      setShowUploadModal(false);
      setSelectedFile(null);
    } catch (error) {
      console.error("Error uploading logo:", error);
      message.error("Failed to upload logo. Please try again.");
    }
  };

  return (
    <>
      <Row gutter={16} style={style}>
        <Col span={12}>
          <div className="logo-upload-container">
            <div className="logo-preview">
              {logoUrl ? (
                <Image
                  src={logoUrl}
                  alt="Company Logo"
                  className="logo-image"
                  preview={true}
                />
              ) : (
                <div className="logo-placeholder">
                  <PlusOutlined className="upload-icon" />
                  <div className="upload-text">No logo uploaded</div>
                  <div className="upload-hint">Upload your company logo</div>
                </div>
              )}
            </div>

            <div className="upload-controls">
              <Upload
                beforeUpload={beforeUpload}
                showUploadList={false}
                accept=".jpg,.jpeg,.png,.webp,.heic"
              >
                <Button icon={<UploadOutlined />} type="primary">
                  {logoUrl ? "Change Logo" : "Upload Logo"}
                </Button>
              </Upload>
              <div className="upload-info">
                Supported formats: JPG, JPEG, PNG, WebP, HEIC
                <br />
                Maximum size: 5MB
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Upload Modal */}
      <Modal
        title="Upload Company Logo"
        open={showUploadModal}
        onCancel={() => {
          setShowUploadModal(false);
          setSelectedFile(null);
        }}
        width={500}
        footer={null}
      >
        <div className="upload-modal-content">
          <p>
            <strong>File Details:</strong>
          </p>
          {selectedFile && (
            <div className="file-details">
              <div>
                <strong>Name:</strong> {selectedFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(1)} KB
              </div>
              <div>
                <strong>Type:</strong> {selectedFile.type}
              </div>
            </div>
          )}

          <div className="file-requirements">
            <p>
              <strong>Allowed file types:</strong>
            </p>
            <p>
              • Images: JPG, JPEG, PNG, WebP, HEIC
              <br />• Max size: 5MB
            </p>
          </div>
        </div>

        <div className="modal-actions">
          <Space>
            <Button
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={uploading}
              disabled={!selectedFile}
              onClick={handleUpload}
              icon={<UploadOutlined />}
            >
              {uploading ? "Uploading..." : "Upload Logo"}
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default LogoUpload;
